import React, { createContext, useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Subscription, TenantInfo } from '../types/models';
import { UsuarioPerfil } from '../types/enums/UsuarioPerfil';
import { TipoUsuario } from '../types/enums/TipoUsuario';
import AuthService from '../services/AuthService';

interface AuthContextData {
  signed: boolean;
  user: User | null;
  subscription: Subscription | null;
  loading: boolean;
  tenants: TenantInfo[];
  selectedTenant: TenantInfo | null;
  needsSelectTenant: boolean;
  signIn(email: string, senha: string): Promise<{ needsPayment: boolean; needsSelectTenant: boolean }>;
  signUp(nome: string, email: string, senha: string, tipoUsuario: TipoUsuario, taxId: string, cellphone?: string): Promise<{ needsPayment: boolean }>;
  signOut(): void;
  selectTenant(tenantId: string): Promise<void>;
  updateUser(user: User): void;
  updateSubscription(subscription: Subscription): void;
  hasRole(role: string): boolean;
  updateUserInfo(user: User): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [selectedTenant, setSelectedTenant] = useState<TenantInfo | null>(null);
  const [needsSelectTenant, setNeedsSelectTenant] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    async function loadStoredData() {
      const storedToken = AuthService.getStoredToken();

      if (storedToken) {
        try {
          AuthService.setAuthHeader(storedToken);

          const userData = await AuthService.renewToken();

          setUser({
            guid: userData.guid,
            name: userData.name,
            email: userData.email,
            role: userData.role || UsuarioPerfil.OWNER,
            roles: userData.roles || [],
            taxId: userData.taxId,
            cellphone: userData.cellphone,
            needsPayment: userData.needsPayment,
            admin: userData.admin || false
          });

          const subscriptionData = userData.subscription || null;
          setSubscription(subscriptionData ? { ...subscriptionData, needsPayment: userData.needsPayment } : null);

          // Setar tenants do renewToken para o company switcher
          if (userData.tenants && userData.tenants.length > 0) {
            setTenants(userData.tenants);
            // Usar tenantId retornado pelo backend para encontrar o tenant atual
            if (userData.tenantId) {
              const currentTenant = userData.tenants.find(
                (t: TenantInfo) => t.tenantId === userData.tenantId
              ) || userData.tenants[0];
              setSelectedTenant(currentTenant);
            } else {
              setSelectedTenant(userData.tenants[0]);
            }
          }

          if (userData.needsPayment === true) {
            navigate('/tw/pagamento');
          }
        } catch (error) {
          console.error('AuthContext: Falha ao renovar token:', error);
        }
      }

      setLoading(false);
    }

    loadStoredData();
  }, []);

  async function signIn(email: string, senha: string) {
    try {
      const result = await AuthService.signIn(email, senha);

      setUser({ ...result.user, needsPayment: result.needsPayment });
      setSubscription(result.subscription ? { ...result.subscription, needsPayment: result.needsPayment } : null);

      // Setar tenants
      if (result.tenants && result.tenants.length > 0) {
        setTenants(result.tenants);
      }

      if (result.needsSelectTenant) {
        setNeedsSelectTenant(true);
        return { needsPayment: false, needsSelectTenant: true };
      }

      // Setar tenant selecionado usando tenantId do backend
      if (result.tenantId && result.tenants) {
        const selected = result.tenants.find((t: TenantInfo) => t.tenantId === result.tenantId) || result.tenants[0];
        setSelectedTenant(selected);
      } else if (result.tenants && result.tenants.length === 1) {
        setSelectedTenant(result.tenants[0]);
      }

      setNeedsSelectTenant(false);
      return { needsPayment: result.needsPayment || false, needsSelectTenant: false };
    } catch (error) {
      throw error;
    }
  }

  async function selectTenant(tenantId: string) {
    try {
      const data = await AuthService.selectTenant(tenantId);

      setUser({
        guid: data.guid,
        name: data.name,
        email: data.email,
        role: data.role || UsuarioPerfil.OWNER,
        roles: data.roles || [],
        taxId: data.taxId,
        cellphone: data.cellphone,
        needsPayment: data.needsPayment,
        admin: data.admin || false
      });

      const subscriptionData = data.subscription || null;
      setSubscription(subscriptionData ? { ...subscriptionData, needsPayment: data.needsPayment } : null);

      // Atualizar tenants e selecionado usando tenantId do backend
      if (data.tenants && data.tenants.length > 0) {
        setTenants(data.tenants);
      }
      const selectedTenantId = data.tenantId || tenantId;
      const selected = (data.tenants || tenants).find((t: TenantInfo) => t.tenantId === selectedTenantId);
      setSelectedTenant(selected || null);
      setNeedsSelectTenant(false);
    } catch (error) {
      throw error;
    }
  }

  async function signUp(nome: string, email: string, senha: string, tipoUsuario: TipoUsuario, taxId: string, cellphone?: string) {
    try {
      const result = await AuthService.signUp(nome, email, senha, tipoUsuario, taxId, cellphone);

      if (result.user) {
        setUser({ ...result.user, needsPayment: result.needsPayment });
      }

      if (result.needsPayment === false) {
        setSubscription({
          id: null,
          status: 'FREE_ACCESS',
          type: null,
          trialStartDate: null,
          trialEndDate: null,
          subscriptionStartDate: null,
          subscriptionEndDate: null,
          hasActiveAccess: true,
          needsPayment: false
        });
      }

      return { needsPayment: result.needsPayment || true };
    } catch (error) {
      throw error;
    }
  }

  function signOut() {
    AuthService.signOut();
    setUser(null);
    setSubscription(null);
    setTenants([]);
    setSelectedTenant(null);
    setNeedsSelectTenant(false);
    navigate('/tw/login');
  }

  function updateUser(updatedUser: User) {
    setUser(updatedUser);
  }

  function updateSubscription(updatedSubscription: Subscription) {
    setSubscription(updatedSubscription);
  }

  function hasRole(role: string): boolean {
    return user?.roles?.includes(role) || false;
  }

  const updateUserInfo = (updatedUser: User) => {
    setUser(updatedUser);
  };

  return (
    <AuthContext.Provider value={{
      signed: !!user,
      user,
      subscription,
      loading,
      tenants,
      selectedTenant,
      needsSelectTenant,
      signIn,
      signUp,
      signOut,
      selectTenant,
      updateUser,
      updateSubscription,
      hasRole,
      updateUserInfo
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}
